// src/components/HeaderBar.tsx
/**
 * HeaderBar — app top chrome with title, role switcher, command palette, settings,
 * and NotificationsBell.
 *
 * Fixes included:
 * - Forces ALL header popovers (incl. NotificationsBell) to open *downward* from the header
 *   instead of "dropping up" and getting clipped at the top of the viewport.
 * - Uses absolute positioning with `top-full right-0` for Role/Shape menus.
 * - Provides a scoped CSS override for the NotificationsBell internals without touching that file.
 * - Raises header z-index to ensure menus render above page content.
 */

import React, { useEffect, useState } from "react";
import { Sparkles, Settings, Shield, Square, Circle } from "lucide-react";
import { get, set } from "@/lib/storage";
import CommandPalette from "@/components/CommandPalette";
import NotificationsBell from "@/components/NotificationsBell";
import { useNavigate } from "react-router-dom";

const ROLES = ["Admin", "Dispatcher", "Loader", "Trucker", "Accountant"] as const;
type Role = (typeof ROLES)[number];

type Props = { title: string };

export default function HeaderBar({ title }: Props) {
  const nav = useNavigate();

  // Role
  const [role, setRole] = useState<Role>(get<Role>("role", "Admin"));

  // Shape (rounded vs square) — default to square as per brief
  const [shape, setShape] = useState<string>(get<string>("ui:shape", "square"));

  // Command palette
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    set("role", role);
  }, [role]);

  useEffect(() => {
    const val = shape === "rounded" ? "rounded" : "square";
    document.documentElement.setAttribute("data-shape", val);
    set("ui:shape", val);
  }, [shape]);

  // Ctrl/Cmd+K to open command palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="border-b bg-white sticky top-0 z-40">
      {/* Scoped CSS to normalize popover placement for all header menus, including NotificationsBell */}
      <style>{`
        /* Ensure any popover inside the header opens DOWN and is visible above content */
        .header-bar :where(details > .popover) {
          position: absolute;
          top: calc(100% + 8px) !important;  /* drop below the summary/button */
          right: 0 !important;
          bottom: auto !important;
          left: auto !important;
          z-index: 60;                        /* above header (z-40) */
          max-height: min(80vh, 28rem);
          overflow: auto;
        }
        /* Avoid clipping within any ancestor; ensure overflow is visible in control rows */
        .header-bar, .header-bar .controls { overflow: visible; }
        /* Remove default summary marker */
        .header-bar summary { list-style: none; }
        .header-bar summary::-webkit-details-marker { display: none; }
      `}</style>

      <div className="h-12 px-3 flex items-center justify-between header-bar">
        {/* Left: Title */}
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="text-lg font-semibold ml-3 sm:ml-4">{title}</h1>
        </div>

        {/* Right: Controls */}
        <div className="controls flex items-center gap-2">
          {/* Command palette */}
          <button
            className="btn-secondary"
            title="Command (Ctrl/Cmd+K)"
            onClick={() => setCmdOpen(true)}
          >
            <Sparkles className="w-4 h-4" />
          </button>

          {/* Role switcher */}
          <div className="relative">
            <details>
              <summary
                className="btn-secondary flex items-center gap-2 px-3 py-1.5"
                title="Switch role (UI only)"
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Role</span>
                <span className="badge">{role}</span>
              </summary>
              {/* Absolute, anchored to the summary via the scoped CSS above */}
              <div className="popover p-2 w-56">
                <div className="text-sm mb-2">Switch role</div>
                <div className="flex flex-col gap-1">
                  {ROLES.map((r) => (
                    <button
                      key={r}
                      className={`btn-secondary w-full justify-between ${
                        r === role ? "ring-1 ring-[var(--brand-600)]" : ""
                      }`}
                      onClick={() => setRole(r)}
                    >
                      <span>{r}</span>
                      {r === role && <span className="badge">Active</span>}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] mt-2 text-slate-500">
                  Role affects which actions are enabled/visible.
                </div>
              </div>
            </details>
          </div>

          {/* Shape toggle */}
          <div className="relative">
            <details>
              <summary
                className="btn-secondary flex items-center gap-2 px-3 py-1.5"
                title="Corner radius"
              >
                {shape === "rounded" ? (
                  <Circle className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">
                  {shape === "rounded" ? "Rounded" : "Square"}
                </span>
              </summary>
              <div className="popover p-2 w-48">
                <button
                  className="btn-secondary w-full mb-2"
                  onClick={() => setShape("square")}
                >
                  Square
                </button>
                <button
                  className="btn-secondary w-full"
                  onClick={() => setShape("rounded")}
                >
                  Rounded
                </button>
              </div>
            </details>
          </div>

          {/* Settings */}
          <button
            className="btn-secondary"
            title="Settings"
            onClick={() => nav("/admin/settings")}
          >
            <Settings className="w-4 h-4" />
          </button>

          {/* Notifications — wrapped in a relative container so its internal popover anchors to this spot.
             The scoped CSS above forces its dropdown to appear BELOW the bell. */}
          <div className="relative">
            <NotificationsBell />
          </div>

          {/* Command palette modal */}
          <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
        </div>
      </div>
    </header>
  );
}
